import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import {
  getClients,
  getClientVehicles,
  getClientPoints,
  updateClient,
  deleteClient,
  grantClientPoints,
} from '../lib/api';
import { formatCurrency, getVehicleTypeLabel } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search,
  Loader2,
  User,
  Phone,
  Mail,
  Star,
  Car,
  History,
  Pencil,
  Trash2,
  AlertTriangle,
  Plus,
  Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';

const emptyEditForm = { name: '', phone: '', email: '' };

export default function AdminClientsPage() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Detail modal
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientVehicles, setClientVehicles] = useState([]);
  const [clientPoints, setClientPoints] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit modal
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Manual points grant (admin only)
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ points: '', note: '' });
  const [grantBusy, setGrantBusy] = useState(false);

  const handleGrantPoints = async (e) => {
    e.preventDefault();
    const n = parseInt(grantForm.points, 10);
    if (Number.isNaN(n) || n === 0) {
      toast.error('Cantidad inválida (debe ser distinta de 0)');
      return;
    }
    if (grantForm.note.trim().length < 3) {
      toast.error('La justificación es obligatoria (mín. 3 caracteres)');
      return;
    }
    setGrantBusy(true);
    try {
      const res = await grantClientPoints(selectedClient.id, n, grantForm.note.trim());
      toast.success(`Puntos actualizados: ${res.data.previous_points} → ${res.data.new_points}`);
      // Refresh detail view
      const [vehiclesRes, pointsRes] = await Promise.all([
        getClientVehicles(selectedClient.id),
        getClientPoints(selectedClient.id),
      ]);
      setClientVehicles(vehiclesRes.data);
      setClientPoints(pointsRes.data);
      setSelectedClient({ ...selectedClient, points: res.data.new_points });
      // Refresh main list
      const all = await getClients();
      setClients(all.data);
      setGrantOpen(false);
      setGrantForm({ points: '', note: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al otorgar puntos');
    }
    setGrantBusy(false);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
    setLoading(false);
  };

  const handleViewClient = async (client) => {
    setSelectedClient(client);
    setLoadingDetail(true);
    
    try {
      const [vehiclesRes, pointsRes] = await Promise.all([
        getClientVehicles(client.id),
        getClientPoints(client.id)
      ]);
      setClientVehicles(vehiclesRes.data);
      setClientPoints(pointsRes.data);
    } catch (error) {

    }
    setLoadingDetail(false);
  };

  const handleOpenEdit = (client) => {
    setEditingClient(client);
    setEditForm({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingClient) return;
    if (!editForm.name.trim() || !editForm.phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await updateClient(editingClient.id, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        email: editForm.email.trim() || null,
      });
      setClients((prev) =>
        prev.map((c) =>
          c.id === editingClient.id
            ? { ...c, name: editForm.name.trim(), phone: editForm.phone.trim(), email: editForm.email.trim() || null }
            : c,
        ),
      );
      toast.success('Cliente actualizado');
      setEditingClient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
    setSaving(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteClient(deleteTarget.id);
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      const extras = [];
      if (res.data.vehicles_deleted) extras.push(`${res.data.vehicles_deleted} vehículo(s)`);
      if (res.data.points_history_deleted) extras.push('historial de puntos');
      toast.success(
        `Cliente eliminado${extras.length ? ` y ${extras.join(', ')}` : ''}`,
      );
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    setDeleting(false);
  };

  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(query) ||
      client.phone.includes(query) ||
      (client.email && client.email.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Clientes</h1>
            <p className="text-slate-600 mt-1">{clients.length} clientes registrados</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="client-search-input"
            />
          </div>
        </div>

        <div className="card-base overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="table-header">Cliente</th>
                <th className="table-header">Teléfono</th>
                <th className="table-header">Email</th>
                <th className="table-header text-right">Puntos</th>
                <th className="table-header text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id} className="table-row" data-testid={`client-row-${client.id}`}>
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="font-medium text-slate-900">{client.name}</span>
                    </div>
                  </td>
                  <td className="table-cell text-slate-600">{client.phone}</td>
                  <td className="table-cell text-slate-600">{client.email || '-'}</td>
                  <td className="table-cell text-right">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <Star className="w-4 h-4" />
                      {client.points}
                    </span>
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewClient(client)}
                        data-testid={`view-client-${client.id}`}
                      >
                        Ver detalle
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(client)}
                        title="Editar"
                        data-testid={`edit-client-${client.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteTarget(client)}
                          title="Eliminar"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredClients.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No se encontraron clientes</p>
            </div>
          )}
        </div>
      </div>

      {/* Client Detail Modal */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del Cliente</DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : selectedClient && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900">{selectedClient.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {selectedClient.phone}
                    </div>
                    {selectedClient.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {selectedClient.email}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-500">Puntos</div>
                  <div className="text-3xl font-bold text-amber-600 flex items-center gap-1 justify-end">
                    <Star className="w-6 h-6" />
                    <span data-testid="client-points-value">{selectedClient.points}</span>
                  </div>
                  {isAdmin && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => { setGrantForm({ points: '', note: '' }); setGrantOpen(true); }}
                      data-testid="grant-points-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Otorgar puntos
                    </Button>
                  )}
                </div>
              </div>
              
              <Tabs defaultValue="vehicles">
                <TabsList>
                  <TabsTrigger value="vehicles">
                    <Car className="w-4 h-4 mr-2" />
                    Vehículos
                  </TabsTrigger>
                  <TabsTrigger value="points">
                    <History className="w-4 h-4 mr-2" />
                    Historial de Puntos
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="vehicles" className="mt-4">
                  {clientVehicles.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <Car className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No hay vehículos registrados</p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {clientVehicles.map((vehicle) => (
                        <div key={vehicle.id} className="p-4 border border-slate-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-slate-900">
                                {getVehicleTypeLabel(vehicle.type)}
                              </div>
                              {vehicle.model && (
                                <div className="text-sm text-slate-600">{vehicle.model}</div>
                              )}
                            </div>
                            <div className="text-right text-sm text-slate-500">
                              {vehicle.plates && <div className="font-mono">{vehicle.plates}</div>}
                              {vehicle.color && <div>{vehicle.color}</div>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="points" className="mt-4">
                  {!clientPoints?.history || clientPoints.history.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <Star className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No hay movimientos de puntos</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {clientPoints.history.map((entry, index) => {
                        const positive = entry.points > 0 || entry.type === 'acumulado';
                        const negative = entry.points < 0 || entry.type === 'redimido';
                        const isManual = entry.type === 'manual';
                        const isReverted = entry.type === 'reversado';
                        const sign = positive && entry.points > 0 ? '+' : negative && entry.points > 0 ? '-' : '';
                        const displayPoints = Math.abs(entry.points);
                        const palette = isReverted
                          ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : isManual
                            ? (entry.points >= 0 ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-rose-50 border-rose-200 text-rose-700')
                            : positive
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'bg-red-50 border-red-200 text-red-700';
                        const label = isReverted
                          ? 'Reversado'
                          : isManual
                            ? (entry.points >= 0 ? 'Otorgado manualmente' : 'Descontado manualmente')
                            : entry.type === 'acumulado' ? 'Puntos ganados' : 'Puntos redimidos';
                        return (
                          <div
                            key={entry.id || `${entry.date}-${entry.type}-${index}`}
                            className={`p-3 rounded-lg border ${palette}`}
                            data-testid={`points-history-${index}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-medium">{label}</div>
                                <div className="text-xs opacity-70">
                                  {new Date(entry.date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  {entry.granted_by_name ? ` · por ${entry.granted_by_name}` : ''}
                                </div>
                                {entry.note && (
                                  <div className="text-xs mt-1 italic opacity-90">"{entry.note}"</div>
                                )}
                              </div>
                              <div className="text-lg font-bold tabular-nums shrink-0">
                                {sign || (entry.points >= 0 ? '+' : '-')}{displayPoints}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Client Modal */}
      <Dialog open={!!editingClient} onOpenChange={(open) => { if (!open) setEditingClient(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Nombre *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                className="mt-1"
                data-testid="edit-client-name"
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Teléfono *</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                required
                className="mt-1"
                data-testid="edit-client-phone"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email (opcional)</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-1"
                data-testid="edit-client-email"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingClient(null)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-edit-client-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Eliminar cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              ¿Seguro que quieres eliminar a <span className="font-semibold text-slate-900">{deleteTarget?.name}</span>?
            </p>
            <p className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              Esta acción <strong>no se puede deshacer</strong>. También se eliminarán sus vehículos, historial de puntos
              y acceso al portal. Las órdenes pasadas permanecerán pero sin cliente asociado.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-client-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant points modal (admin only) */}
      <Dialog open={grantOpen} onOpenChange={(v) => { if (!v) { setGrantOpen(false); setGrantForm({ points: '', note: '' }); } }}>
        <DialogContent className="max-w-md" data-testid="grant-points-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Star className="w-5 h-5" />
              Otorgar / ajustar puntos
            </DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
              <div className="flex justify-between"><span className="text-slate-500">Cliente:</span><span className="font-semibold">{selectedClient.name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Puntos actuales:</span><span className="font-mono font-semibold text-amber-600">{selectedClient.points}</span></div>
            </div>
          )}
          <form onSubmit={handleGrantPoints} className="space-y-3">
            <div>
              <Label htmlFor="grant-points">Cantidad de puntos</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button type="button" size="icon" variant="outline" onClick={() => setGrantForm({ ...grantForm, points: String(Math.abs(parseInt(grantForm.points || '0', 10)) || 1) })} title="Sumar" data-testid="grant-positive">
                  <Plus className="w-4 h-4 text-emerald-600" />
                </Button>
                <Input
                  id="grant-points"
                  type="number"
                  value={grantForm.points}
                  onChange={(e) => setGrantForm({ ...grantForm, points: e.target.value })}
                  placeholder="Ej: 50 (o -20 para restar)"
                  className="text-center font-mono text-lg"
                  autoFocus
                  data-testid="grant-points-input"
                />
                <Button type="button" size="icon" variant="outline" onClick={() => setGrantForm({ ...grantForm, points: String(-Math.abs(parseInt(grantForm.points || '0', 10) || 1)) })} title="Restar" data-testid="grant-negative">
                  <Minus className="w-4 h-4 text-rose-600" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Usa valores negativos para descontar puntos (ej: <code>-20</code>). No puede quedar por debajo de 0.</p>
            </div>
            <div>
              <Label htmlFor="grant-note">Justificación <span className="text-red-500">*</span></Label>
              <Textarea
                id="grant-note"
                value={grantForm.note}
                onChange={(e) => setGrantForm({ ...grantForm, note: e.target.value })}
                placeholder="Ej: cliente recurrente, compensación, promoción especial..."
                rows={3}
                className="mt-1"
                data-testid="grant-note-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setGrantOpen(false); setGrantForm({ points: '', note: '' }); }} disabled={grantBusy}>Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={grantBusy} data-testid="confirm-grant-points-btn">
                {grantBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
}

